import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MailPlus, ArrowRight, Mail, Loader2 } from "lucide-react";
import { CvShell } from "@/components/cv/CvShell";
import { CvGate } from "@/components/cv/CvGate";
import { useAuth } from "@/context/AuthContext";
import { cvApi } from "@/lib/api";

export default function CvLetters() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [cvs, setCvs] = useState([]);
  const [pickCv, setPickCv] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!user) return;
    cvApi.letterList().then((d) => setItems(d.items || [])).catch(() => setItems([]));
    cvApi.list().then((d) => setCvs(d.items || [])).catch(() => {});
  }, [user]);

  const createLetter = async () => {
    setCreating(true);
    try {
      const l = await cvApi.letterCreate({ cv_id: pickCv || "" });
      navigate(`/cv/list/${l.id}`);
    } catch {
      toast.error("Nie udało się utworzyć listu. Spróbuj ponownie.");
      setCreating(false);
    }
  };

  return (
    <CvShell active="listy">
      <div data-testid="cv-letters" className="pb-6">
        <h1 className="font-display text-2xl font-semibold text-brand-text sm:text-3xl">Listy motywacyjne</h1>
        <p className="mt-2 max-w-2xl text-brand-sec">Przygotuj list motywacyjny dopasowany do oferty. Treść powstaje na podstawie danych z Twojego CV — bez zmyślania faktów.</p>

        {!user ? (
          <div className="mt-8 max-w-2xl"><CvGate returnTo="/cv/listy" message="Załóż bezpłatne konto, aby tworzyć listy motywacyjne." /></div>
        ) : (
          <>
            {/* Nowy list */}
            <div className="mt-6 rounded-panel border border-brand-line bg-brand-surface p-5" data-testid="cv-letter-new">
              <p className="text-sm font-semibold text-brand-text">Nowy list motywacyjny</p>
              <p className="mt-1 text-[13px] text-brand-muted">Wybierz CV, z którego pobierzemy dane kontaktowe i doświadczenie (opcjonalnie).</p>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <select
                  data-testid="cv-letter-cv-select"
                  value={pickCv}
                  onChange={(e) => setPickCv(e.target.value)}
                  className="min-w-[240px] rounded-button border border-brand-line bg-brand-bg px-3 py-2.5 text-sm text-brand-text"
                >
                  <option value="">Bez powiązania z CV</option>
                  {cvs.map((c) => (<option key={c.id} value={c.id}>{c.title}</option>))}
                </select>
                <button type="button" data-testid="cv-letter-create" disabled={creating} onClick={createLetter} className="inline-flex items-center gap-2 rounded-button bg-brand-accent px-5 py-2.5 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5 disabled:opacity-50">
                  {creating ? <Loader2 size={16} className="animate-spin" /> : <MailPlus size={16} />} Utwórz list
                </button>
              </div>
            </div>

            {items === null ? (
              <div className="mt-10 flex justify-center"><Loader2 className="animate-spin text-brand-accent" /></div>
            ) : items.length === 0 ? (
              <div className="mt-6 flex flex-col items-center justify-center gap-3 rounded-panel border border-dashed border-brand-line px-6 py-12 text-center" data-testid="cv-letters-empty">
                <Mail size={30} className="text-brand-line" />
                <p className="text-brand-text">Nie masz jeszcze żadnych listów motywacyjnych.</p>
              </div>
            ) : (
              <ul className="mt-6 space-y-3" data-testid="cv-letters-list">
                {items.map((it) => (
                  <li key={it.id} className="rounded-panel border border-brand-line bg-brand-surface transition-colors hover:border-brand-accent/50">
                    <Link to={`/cv/list/${it.id}`} data-testid="cv-letters-item" className="flex items-center gap-4 p-4">
                      <span className="flex h-10 w-10 items-center justify-center rounded-card border border-brand-line text-brand-accent"><Mail size={18} /></span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-brand-text">{it.title}</p>
                        <p className="text-[12.5px] text-brand-muted">Zmieniono: {new Date(it.updated_at).toLocaleString("pl-PL")}</p>
                      </div>
                      <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${it.status === "ready" ? "bg-brand-accent/15 text-brand-accent" : "border border-brand-line text-brand-sec"}`}>
                        {it.status === "ready" ? "Gotowy" : "Wersja robocza"}
                      </span>
                      <ArrowRight size={16} className="shrink-0 text-brand-muted" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </CvShell>
  );
}
